/**
 * GA4 Product Analytics via gtag.js
 *
 * Thin wrapper around the gtag.js snippet already loaded in index.html.
 * Every call is wrapped in try/catch so analytics failures never break the app.
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

function gtag(...args: unknown[]) {
  try {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag(...args);
    }
  } catch {
    // Analytics should never break the app
  }
}

// GA4 destination. Scoping product events to this ID keeps them out of the
// Google Ads account (AW-17886715257) — without an explicit send_to, gtag
// broadcasts every event to ALL configured destinations, which would surface
// GA4 events like "Account Sign Up Completed" inside Google Ads. Only the
// dedicated conversion event (trackAdsSignupConversion) targets the Ads ID.
const GA4_MEASUREMENT_ID = 'G-F5SM5KWB8Z';

// ── Core helpers ───────────────────────────────────────────────

export function trackEvent(eventName: string, params?: Record<string, unknown>) {
  gtag('event', eventName, { send_to: GA4_MEASUREMENT_ID, ...params });
}

export function setAnalyticsUserId(userId: string) {
  gtag('set', { user_id: userId });
}

export function setAnalyticsUserProperties(properties: Record<string, unknown>) {
  gtag('set', 'user_properties', properties);
}

// ── Typed event helpers ────────────────────────────────────────

export function trackAccountSignupCompleted(signupMethod: 'google' | 'email' | 'apple') {
  trackEvent('Account_signup_completed', { signup_method: signupMethod });
}

export function trackAdsSignupConversion(email?: string) {
  const userData = email ? { email: email.trim().toLowerCase() } : undefined;
  gtag('event', 'conversion', {
    send_to: 'AW-17886715257/f-_OCJXw46kcEPm6htFC',
    ...(userData ? { user_data: userData } : {}),
  });
}

export function trackDashboardViewed(accountState: 'no_trial' | 'trial_active' | 'trial_expired') {
  trackEvent('Dashboard_viewed', { account_state: accountState });
}

export function trackFlowCreationStarted(source: 'dashboard' | 'template' | 'duplicate') {
  trackEvent('Flow_creation_started', { source });
}

export function trackFlowBuilderStepViewed(
  stepName: 'product_details' | 'flow_type' | 'design' | 'thank_you' | 'publish'
) {
  trackEvent('Flow_builder_step_viewed', { step_name: stepName });
}

export function trackFlowDraftSaved(
  stepName: 'product_details' | 'flow_type' | 'design' | 'thank_you',
  flowType: string
) {
  trackEvent('Flow_draft_saved', { step_name: stepName, flow_type: flowType });
}

export function trackFlowBuilderCompleted(flowType: string) {
  trackEvent('Flow_builder_completed', { flow_type: flowType });
}

export function trackTrialPaywallViewed(flowType: string) {
  trackEvent('Trial_paywall_viewed', { source: 'publish_flow', flow_type: flowType });
}

export function trackTrialCheckoutStarted(planType: 'monthly' | 'yearly') {
  trackEvent('Trial_checkout_started', { plan_type: planType });
}

export function trackTrialStarted(planType: 'monthly' | 'yearly', trialDays = 14) {
  trackEvent('Trial_started', { plan_type: planType, trial_days: trialDays });
}

export function trackFlowPublished(flowType: string, publishMethod: 'direct_link' | 'embed' = 'direct_link') {
  trackEvent('Flow_published', { flow_type: flowType, publish_method: publishMethod });
}
